// Who we are page

import { useNavigate } from "react-router-dom";
import HeadernDescription from "../components/Common/HeadernDescription";
import LandingHero from "../components/Common/LandingHero";
import data from "../content.json";
import { Box, WifiOff, ShieldAlert, Database } from "lucide-react";
import DigitalInnovation from "../components/Home/DigitalInnovation";
import Power from "../components/EDG/Power";
import CTASection from "../components/Common/CtaSection";
const {
  sec4HeadingPara,
  keyServices4,
  sec5HeadingPara,
  sec6HeadingPara,
  keyService6,
  cta,
} = data.edg;

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <>
      <div className="relative z-20 overflow-visible">
        <LandingHero data={data.edg.hero} />
      </div>

      <div className="mx-auto px-6 md:py-20  xl:px-36">
        {/* Header Section */}
        <div className="max-w-4xl mb-12">
          <h2
            className="mb-4 font-medium"
            style={{
              color: "#1E2A5A",
              fontSize: "clamp(1.8rem, 2.5vw, 2.6rem)",
            }}
          >
            The Challenge of Connected Healthcare
          </h2>

          <p className="leading-relaxed text-[#5569A0]">
            Modern healthcare systems generate vast amounts of data across
            devices and platforms—but without a unified infrastructure, this
            data remains fragmented, delayed, and difficult to act on.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Card 1 */}
          <div className="p-6 rounded-xl bg-[#F4F6FF]">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-4 shadow-sm">
              <Box className="text-orange-500 w-5 h-5" />
            </div>
            <h3 className="text-[#1E2A5A] font-medium mb-2">
              Fragmented Device Ecosystems
            </h3>
            <p className="text-[#5569A0] leading-relaxed">
              Healthcare environments rely on a wide range of devices—each
              operating on different protocols, standards, and systems. This
              fragmentation creates silos, making it difficult to integrate data
              seamlessly and build a unified view of patient information.
            </p>
          </div>

          {/* Card 2 */}
          <div className="p-6 rounded-xl bg-[#F4F6FF]">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-4 shadow-sm">
              <Database className="text-orange-500 w-5 h-5" />
            </div>
            <h3 className="text-[#1E2A5A] font-medium mb-2">
              Data Overload & Latency
            </h3>
            <p className="text-[#5569A0]leading-relaxed">
              Continuous streams of data from medical devices and sensors can
              overwhelm networks and systems. Without efficient processing at
              the edge, this leads to delays, increased bandwidth usage, and
              slower real-time clinical decision-making.
            </p>
          </div>

          {/* Card 3 */}
          <div className="p-6 rounded-xl bg-[#F4F6FF]">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-4 shadow-sm">
              <ShieldAlert className="text-orange-500 w-5 h-5" />
            </div>
            <h3 className="text-[#1E2A5A] font-medium mb-2">
              Security & Compliance Risks
            </h3>
            <p className="text-[#5569A0] leading-relaxed">
              Connected devices introduce multiple entry points into healthcare
              networks. Without robust security layers, sensitive patient data
              becomes vulnerable, increasing the risk of breaches and
              non-compliance with regulatory standards.
            </p>
          </div>

          {/* Card 4 */}
          <div className="p-6 rounded-xl bg-[#F4F6FF]">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-4 shadow-sm">
              <WifiOff className="text-orange-500 w-5 h-5" />
            </div>
            <h3 className="text-[#1E2A5A] font-medium mb-2">
              Limited Interoperability
            </h3>
            <p className="text-[#5569A0] leading-relaxed">
              Legacy healthcare systems often lack the flexibility to integrate
              with modern digital platforms. This limits data exchange, disrupts
              workflows, and prevents organizations from fully leveraging
              connected care technologies.
            </p>
          </div>
        </div>
      </div>
      {/* 4th Section */}
      <div className="md:px-10 py-10 md:py-20">
        <HeadernDescription
          section={sec4HeadingPara}
          align="center"
        ></HeadernDescription>
        <DigitalInnovation
          section={keyServices4}
          columns={5}
          arrowIcon={true}
          imgHeight="17vh"
          cardHeight="26vh"
        />
      </div>
      <div className="md:px-10 py-10 md:py-20 bluebg">
        <HeadernDescription
          section={sec5HeadingPara}
          align="center"
        ></HeadernDescription>
        <Power></Power>
      </div>

      {/* 6th Section */}
      <div className="md:px-10 py-10 md:py-20 xl:px-36">
        <HeadernDescription
          section={sec6HeadingPara}
          align="left"
        ></HeadernDescription>

        <div className="mx-auto">
          {/* Grid Container */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-8">
            {keyService6.map((item) => (
              <div
                key={item.id}
                className="flex flex-col md:flex-row overflow-hidden rounded-2xl shadow-sm border border-slate-100"
              >
                {/* Left: Image (40% width) */}
                <div className="w-full md:w-[40%] h-64 md:h-auto">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Right: Content Area (60% width) */}
                <div className="relative w-full md:w-[60%] bg-[#F8FAFF] p-8 md:p-10 flex flex-col justify-center sm:h-44">
                  {/* Content (Z-index ensure text is above number) */}
                  <div className="relative z-10">
                    <h3 className="font-bold  mb-3">{item.title}</h3>
                    <p className=" text-[#5569A0] leading-relaxed max-w-[240px]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection {...cta} />
      {/* <CTASection
        reverse={true}
        heading="Scalable Solutions"
        description="Our platform grows with you, ensuring seamless integration across all your clinical workflows."
        imageSrc="/assets/home/features.png"
        buttonText="View Features"
        buttonLink="/features"
        bgColor="bg-white"
      /> */}
    </>
  );
};

export default AboutUs;
